import React, { useCallback, useEffect } from 'react';
import { useUser } from '@/hooks/auth/use-user';
import { useThreads } from './hooks/useThreads';
import { useAIAgentChat } from '@/contexts/ai-agents';
import { useChatManager } from './useChatManager';
import { useLocalStorageState } from './useLocalStorageState';
import { logInfo, LogCategory } from '@/utils/logger';

/**
 * useAIAgentInterface - Simplified integration layer
 * 
 * ✅ SIMPLIFIED: Removed auto-initialization (ChatManager handles this)
 * ✅ CLEAN: Only provides stable operation references
 * ✅ REACTIVE: All state comes from localStorage via ChatManager
 */
export const useAIAgentInterface = () => {
  const { user } = useUser();
  const { threads, isLoading: isLoadingThreads } = useThreads();
  const { currentAssistant } = useAIAgentChat();
  
  // Business logic layer
  const chatManager = useChatManager(user, currentAssistant);
  
  // UI state layer - pure localStorage
  const uiState = useLocalStorageState();

  // ✅ REMOVED: Auto-initialization logic - ChatManager handles this automatically

  // ✅ STABLE OPERATION: Switch to Chat Tab
  const switchToChatTab = useCallback(async (targetThreadId?: string): Promise<void> => {
    try {
      logInfo('AIAgentInterface: Switching to chat tab', {
        category: LogCategory.USER_ACTION,
        data: { targetThreadId: targetThreadId || 'new' }
      });

      // 1. Load thread first (business logic) - ChatManager auto-loads messages
      const loadedThreadId = await chatManager.loadThread(targetThreadId);
      
      if (loadedThreadId) {
        // 2. Update UI state and persist thread ID
        uiState.setActiveTab('chat');
        uiState.setCurrentThreadId(loadedThreadId);

        logInfo('AIAgentInterface: Successfully switched to chat tab', {
          category: LogCategory.STATE,
          data: { threadId: loadedThreadId }
        });
      } else {
        // If thread loading failed, stay on threads tab
        logInfo('AIAgentInterface: Thread loading failed, staying on threads tab', {
          category: LogCategory.STATE
        });
        uiState.setActiveTab('threads');
      }
    } catch (error: any) {
      logInfo('AIAgentInterface: Failed to switch to chat tab, falling back to threads', {
        category: LogCategory.STATE,
        data: { error: error.message }
      });
      uiState.setActiveTab('threads');
    }
  }, [chatManager.loadThread, uiState.setActiveTab, uiState.setCurrentThreadId]);

  // ✅ STABLE OPERATION: Switch to Threads Tab
  const switchToThreadsTab = useCallback(() => {
    logInfo('AIAgentInterface: Switching to threads tab', {
      category: LogCategory.USER_ACTION
    });
    
    // ✅ CLEAN: Only update UI state - persistence handled by localStorage
    uiState.setActiveTab('threads');
  }, [uiState.setActiveTab]);

  return {
    // ✅ REACTIVE STATE: All from localStorage via ChatManager/UIState
    activeTab: uiState.activeTab,
    inputValue: uiState.inputValue,
    currentThreadId: uiState.currentThreadId,
    
    // Chat state from ChatManager
    messages: chatManager.messages,
    chatState: chatManager.chatState,
    isGenerating: chatManager.isGenerating,
    error: chatManager.error,
    
    // Thread data
    threads,
    isLoadingThreads,
    
    // ✅ STABLE OPERATIONS: Clean, focused operations
    switchToChatTab,
    switchToThreadsTab,
    
    // UI operations
    setInputValue: uiState.setInputValue,
    clearInput: uiState.clearInput,
    applyPrompt: uiState.applyPrompt,
    
    // Chat operations from ChatManager
    loadThread: chatManager.loadThread,
    clearChat: chatManager.clearChat,
    resetError: chatManager.resetError,
    isThreadLoaded: chatManager.isThreadLoaded
  };
}; 